import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { operatorsAPI } from '../services/api';

const FALLBACK_PROFILES = {
  super_admin: { label: 'Super administrateur', permissions: [] },
  admin_ops: { label: 'Administrateur operations', permissions: [] },
  superviseur: { label: 'Superviseur support', permissions: [] },
  support_n2: { label: 'Support niveau 2', permissions: [] },
  support_n1: { label: 'Support niveau 1', permissions: [] },
};

export default function OperatorsPage() {
  const [operators, setOperators] = useState([]);
  const [profiles, setProfiles] = useState(FALLBACK_PROFILES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', role: 'support_n1' });

  const profileEntries = useMemo(() => Object.entries(profiles || FALLBACK_PROFILES), [profiles]);

  async function loadOperators() {
    setLoading(true);
    try {
      const { data } = await operatorsAPI.list();
      if (!data.ok) throw new Error(data.error || 'Chargement impossible');
      setOperators(data.operators || []);
      setProfiles(data.profiles || FALLBACK_PROFILES);
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Erreur chargement utilisateurs');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadOperators(); }, []);

  async function saveOperator(event) {
    event.preventDefault();
    const username = form.username.trim();
    if (!username) return toast.error('Identifiant requis');
    if (!form.password && !operators.some((op) => op.username === username)) {
      return toast.error('Mot de passe temporaire requis pour un nouveau compte');
    }

    setSaving(true);
    try {
      const payload = { username, role: form.role };
      if (form.password) payload.password = form.password;
      const { data } = await operatorsAPI.save(payload);
      if (!data.ok) throw new Error(data.error || 'Enregistrement impossible');
      toast.success('Utilisateur enregistre');
      setForm({ username: '', password: '', role: 'support_n1' });
      await loadOperators();
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  }

  function editOperator(operator) {
    setForm({ username: operator.username, password: '', role: operator.role || 'support_n1' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Utilisateurs & profils</h1>
          <p style={S.subtitle}>Creation des comptes support et attribution des profils metier.</p>
        </div>
        <button style={S.refreshBtn} onClick={loadOperators} disabled={loading}>Rafraichir</button>
      </div>

      <div style={S.grid}>
        <form style={S.card} onSubmit={saveOperator}>
          <h2 style={S.cardTitle}>Creer / modifier un utilisateur</h2>
          <label style={S.field}>
            <span style={S.label}>Identifiant</span>
            <input style={S.input} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="ex: boukarops" />
          </label>
          <label style={S.field}>
            <span style={S.label}>Mot de passe temporaire</span>
            <input style={S.input} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="minimum 10 caracteres" />
            <span style={S.help}>Laissez vide pour modifier seulement le profil d'un compte existant.</span>
          </label>
          <label style={S.field}>
            <span style={S.label}>Profil metier</span>
            <select style={S.input} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {profileEntries.map(([key, profile]) => <option key={key} value={key}>{profile.label || key}</option>)}
            </select>
          </label>
          <button style={S.primaryBtn} disabled={saving}>{saving ? 'Enregistrement...' : 'Enregistrer'}</button>
        </form>

        <div style={S.card}>
          <h2 style={S.cardTitle}>Profils disponibles</h2>
          <div style={S.profileList}>
            {profileEntries.map(([key, profile]) => (
              <div key={key} style={S.profileItem}>
                <div style={S.profileName}>{profile.label || key}</div>
                <div style={S.profileKey}>{key}</div>
                <div style={S.permissions}>{(profile.permissions || []).join(' · ') || 'permissions definies cote serveur'}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={S.card}>
        <h2 style={S.cardTitle}>Comptes existants</h2>
        {loading ? <div style={S.empty}>Chargement...</div> : (
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Identifiant</th>
                  <th style={S.th}>Profil</th>
                  <th style={S.th}>Permissions</th>
                  <th style={S.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {operators.map((operator) => (
                  <tr key={operator.username}>
                    <td style={S.td}><b>{operator.username}</b></td>
                    <td style={S.td}><span style={S.roleBadge}>{operator.roleLabel || operator.role}</span></td>
                    <td style={S.tdSmall}>{(operator.permissions || []).join(' · ')}</td>
                    <td style={S.td}><button style={S.smallBtn} onClick={() => editOperator(operator)}>Modifier</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!operators.length && <div style={S.empty}>Aucun compte operateur.</div>}
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  page: { padding: 20, color: '#ffffff' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 18 },
  title: { margin: 0, fontSize: 24, letterSpacing: .2 },
  subtitle: { margin: '6px 0 0', color: 'rgba(255,255,255,.82)', fontSize: 12 },
  grid: { display: 'grid', gridTemplateColumns: 'minmax(320px,420px) 1fr', gap: 16, marginBottom: 16 },
  card: { background: '#10224a', border: '1px solid rgba(255,255,255,.16)', borderRadius: 8, padding: 16, boxShadow: '0 18px 40px rgba(0,0,0,.16)' },
  cardTitle: { margin: '0 0 14px', fontSize: 15, color: '#ffffff' },
  field: { display: 'grid', gap: 7, marginBottom: 13 },
  label: { fontSize: 11, color: '#ffffff', textTransform: 'uppercase', letterSpacing: .7, fontWeight: 800 },
  input: { width: '100%', background: '#0d1b3e', border: '1px solid rgba(255,255,255,.22)', color: '#ffffff', borderRadius: 6, padding: '10px 12px', outline: 'none', fontFamily: 'monospace', fontSize: 13 },
  help: { fontSize: 11, color: 'rgba(255,255,255,.82)' },
  primaryBtn: { width: '100%', background: '#f5a623', color: '#0d1b3e', border: 'none', borderRadius: 6, padding: '11px 14px', fontWeight: 900, cursor: 'pointer' },
  refreshBtn: { background: '#1e4db7', color: '#ffffff', border: 'none', borderRadius: 6, padding: '9px 14px', fontWeight: 800, cursor: 'pointer' },
  profileList: { display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 10 },
  profileItem: { background: '#0d1b3e', border: '1px solid rgba(255,255,255,.14)', borderRadius: 6, padding: 12 },
  profileName: { color: '#ffffff', fontSize: 13, fontWeight: 900 },
  profileKey: { color: '#f5a623', fontSize: 11, marginTop: 3 },
  permissions: { color: 'rgba(255,255,255,.86)', fontSize: 10, lineHeight: 1.5, marginTop: 8 },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { color: '#ffffff', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: .8, borderBottom: '1px solid rgba(255,255,255,.16)', padding: '10px 12px' },
  td: { color: '#ffffff', borderBottom: '1px solid rgba(255,255,255,.10)', padding: '11px 12px', fontSize: 13 },
  tdSmall: { color: 'rgba(255,255,255,.86)', borderBottom: '1px solid rgba(255,255,255,.10)', padding: '11px 12px', fontSize: 11, lineHeight: 1.5 },
  roleBadge: { display: 'inline-block', background: 'rgba(245,166,35,.13)', color: '#f5a623', border: '1px solid rgba(245,166,35,.32)', borderRadius: 999, padding: '4px 9px', fontSize: 11, fontWeight: 800 },
  smallBtn: { background: 'rgba(255,255,255,.08)', color: '#ffffff', border: '1px solid rgba(255,255,255,.18)', borderRadius: 5, padding: '6px 10px', cursor: 'pointer' },
  empty: { color: 'rgba(255,255,255,.86)', padding: 14, fontSize: 12 },
};
